// node_modules
import { FC, useContext, useEffect, useState, CSSProperties, useCallback } from "react";
// Components
import { FindestTextBox } from "Components";
import { Synonym } from "./Synonym";
// Types
import { TActionObjectDTO, TEnvironmentVariableDTO, TSavedQueryTempDTO, TSpecificationUnitDTO, TSynonymDTO, T2dCoordinate } from "Types";
// Style
import styles from "./synonym.module.scss";
// Controllers
import { SynonymControllerSingleton } from "Controllers";
// Enums
import { SynonymTypeEnum } from "Enums";
// Contexts
import { SavedQueryContext } from "Providers";
import { CaseContext } from "Stores";
import { GeneratedSynonymsPopup } from "./GeneratedSynonymsPopup";
import { FeatureToggleConstants } from "Constants";
import { CreateSynonymGroup } from "./CreateSynonymGroup";

// TODO: Maybe do something about the props waterfall
type TSynonymsProps = {
    synonyms: TSynonymDTO[],
    synonymType: SynonymTypeEnum,
    parentElementId: number,
    parentElementText: string,
    isOpen: boolean,
    clickedParentElement?: { element: EventTarget },
    isPartOfTermGroup?: boolean,
    mainTermGroupValue?: string
}

export const Synonyms: FC<TSynonymsProps> = ({ synonyms, synonymType, parentElementId, parentElementText, isOpen, 
        clickedParentElement, isPartOfTermGroup, mainTermGroupValue }: TSynonymsProps) => {
    // Contexts
    const { savedQuery, duplicateSavedQuery, updateSynonyms } = useContext(SavedQueryContext);
    const { caseState } = useContext(CaseContext);

    // State
    const [isGenerationPopupOpen, setIsGenerationPopupOpen] = useState(isOpen);
    const [dropdownListPosition, setDropdownListPosition] = useState<T2dCoordinate>({x: 0, y: 0});
    const [parentSynonymText, setParentSynonymText] = useState(parentElementText);

    // UseEffect
    useEffect(() => {
        if(isOpen) {
            setIsGenerationPopupOpen(isOpen);
        }
    }, [isOpen]);
    
    
    const calculateDropdownListPosition = useCallback((target: EventTarget): void => {
        const htmlTarget = target as HTMLElement;
        setDropdownListPosition({x: htmlTarget.getBoundingClientRect().right, y: htmlTarget.getBoundingClientRect().top});
        setIsGenerationPopupOpen(true);
    }, []);

    useEffect(() => {
        if(clickedParentElement) {
            calculateDropdownListPosition(clickedParentElement.element);
        }
    }, [calculateDropdownListPosition, clickedParentElement]);

    useEffect(() => {
        if(clickedParentElement) {
            setParentSynonymText(parentElementText);
            calculateDropdownListPosition(clickedParentElement.element);
        }
    }, [calculateDropdownListPosition, clickedParentElement, parentElementText]);

    // Logic
    const getSynonymParentElementInSavedQuery = (typeOfSynonym: SynonymTypeEnum, parentElementId: number, savedQuery: TSavedQueryTempDTO): TActionObjectDTO | TEnvironmentVariableDTO | TSpecificationUnitDTO | undefined => {
        // safety-checks
        if (typeOfSynonym === undefined || typeOfSynonym === null || !parentElementId || !savedQuery) return undefined;

        switch (typeOfSynonym) {
            case SynonymTypeEnum.Action:
            case SynonymTypeEnum.Object:
                if (!savedQuery.actionObjects) return undefined;
                return savedQuery.actionObjects.filter(actionObject => actionObject.id === parentElementId)[0];
            case SynonymTypeEnum.Environment:
                if (!savedQuery.environmentVariables) return undefined;
                return savedQuery.environmentVariables.filter(envVar => envVar.id === parentElementId)[0];
            case SynonymTypeEnum.Unit:
                if (!savedQuery.specificationUnits) return undefined;
                return savedQuery.specificationUnits.filter(specUnit => specUnit.id === parentElementId)[0];
            default:
                return undefined;
        }
    };

    const getParentElementSynonymList = (typeOfSynonym: SynonymTypeEnum, parentElement: TActionObjectDTO | TEnvironmentVariableDTO | TSpecificationUnitDTO): TSynonymDTO[] | undefined => {
        // safety-checks
        if (typeOfSynonym === undefined || typeOfSynonym === null || !parentElement) return undefined;

        switch (typeOfSynonym) {
            case SynonymTypeEnum.Action:
                return (parentElement as TActionObjectDTO).actionSynonyms;
            case SynonymTypeEnum.Object:
                return (parentElement as TActionObjectDTO).objectSynonyms;
            case SynonymTypeEnum.Environment:
                return (parentElement as TEnvironmentVariableDTO).synonyms;
            case SynonymTypeEnum.Unit:
                return (parentElement as TSpecificationUnitDTO).unitSynonyms;
            default:
                return undefined;
        }
    };

    const findDuplicatedParentElementIdInSavedQuery = (typeOfSynonym: SynonymTypeEnum, parentElement: TActionObjectDTO | TEnvironmentVariableDTO | TSpecificationUnitDTO, savedQuery: TSavedQueryTempDTO): TActionObjectDTO | TEnvironmentVariableDTO | TSpecificationUnitDTO | undefined => {
        // safety-checks
        if (typeOfSynonym === undefined || typeOfSynonym === null || !parentElementId || !savedQuery) return undefined;

        switch (typeOfSynonym) {
            case SynonymTypeEnum.Action:
            case SynonymTypeEnum.Object: {
                if (!savedQuery.actionObjects) return undefined;
                const duplicatedActionObject: TActionObjectDTO | undefined =
                    savedQuery.actionObjects.filter(actionObject =>
                        actionObject.action.trim().toLowerCase() === (parentElement as TActionObjectDTO).action.trim().toLowerCase() &&
                        actionObject.dobject.trim().toLowerCase() === (parentElement as TActionObjectDTO).dobject.trim().toLowerCase())[0];
                if (!duplicatedActionObject) return undefined;
                return duplicatedActionObject;
            }
            case SynonymTypeEnum.Environment: {
                if (!savedQuery.environmentVariables) return undefined;
                const duplicatedEnvironmentVariable: TEnvironmentVariableDTO | undefined =
                    savedQuery.environmentVariables.filter(envVar =>
                        envVar.environment.trim().toLowerCase() === (parentElement as TEnvironmentVariableDTO).environment.trim().toLowerCase())[0];
                if (!duplicatedEnvironmentVariable) return undefined;
                return duplicatedEnvironmentVariable;
            }
            case SynonymTypeEnum.Unit: {
                if (!savedQuery.specificationUnits) return undefined;
                const duplicatedSpecificationUnit: TSpecificationUnitDTO | undefined =
                    savedQuery.specificationUnits.filter(specUnit =>
                        specUnit.specification.trim().toLowerCase() === (parentElement as TSpecificationUnitDTO).specification.trim().toLowerCase() &&
                        specUnit.unit.trim().toLowerCase() === (parentElement as TSpecificationUnitDTO).unit.trim().toLowerCase())[0];
                if (!duplicatedSpecificationUnit) return undefined;
                return duplicatedSpecificationUnit;
            }
            default:
                return undefined;
        }
    };

    const findDuplicatedSynonymInSynonymList = (synonymToFind: string, synonymList: TSynonymDTO[]): TSynonymDTO | undefined => {
        // safety-checks
        if (!synonymToFind || !synonymList) return undefined;

        for (const synonym of synonymList) {
            if (synonym.text.trim().toLowerCase() === synonymToFind.trim().toLowerCase()) return synonym;
        }

        return undefined;
    };

    const getSynonymByIdInList = (id: number, synonymList: TSynonymDTO[]): TSynonymDTO | undefined => {
        // safety-checks
        if (id === null || id === undefined || !synonymList) return undefined;

        for (const synonym of synonymList) {
            if (synonym.id === id) return synonym;
        }

        return undefined;
    };

    const addSynonym = async (newSynonymText: string): Promise<void> => {
        // safety-checks
        if (!savedQuery) return;

        // remove white spaces at the start and end of the new synonym
        newSynonymText = newSynonymText.trim();

        // if synonym was already added, do nothing
        if (findDuplicatedSynonymInSynonymList(newSynonymText, synonyms) !== undefined) return;

        const foundParentElement: TActionObjectDTO | TEnvironmentVariableDTO | TSpecificationUnitDTO | undefined =
            getSynonymParentElementInSavedQuery(synonymType, parentElementId, savedQuery);
        // safety-checks
        if (!foundParentElement) return;

        if (savedQuery.isUserAdded && savedQuery.isOwnedByCurrentUser) {
            // init new synonym
            const newSynonym = await SynonymControllerSingleton.createAsync(newSynonymText, synonymType, parentElementId, true);
            // update synonym list of parent element in the saved query context
            updateSynonyms(synonymType, foundParentElement, [...synonyms, newSynonym]);
        } else {
            duplicateSavedQuery(caseState.selectedOriginalTechnologyId, caseState.selectedCaseId, async (duplicatedSavedQuery: TSavedQueryTempDTO): Promise<void> => {
                // safety-checks
                if (!duplicatedSavedQuery) return;

                const duplicatedParentElement: TActionObjectDTO | TEnvironmentVariableDTO | TSpecificationUnitDTO | undefined =
                    findDuplicatedParentElementIdInSavedQuery(synonymType, foundParentElement, duplicatedSavedQuery);
                // safety-checks
                if (!duplicatedParentElement) return;

                const duplicatedParentElementSynonymList: TSynonymDTO[] | undefined =
                    getParentElementSynonymList(synonymType, duplicatedParentElement);
                // safety-checks
                if (!duplicatedParentElementSynonymList) return;

                // init new synonym
                const newSynonym = await SynonymControllerSingleton.createAsync(newSynonymText, synonymType, duplicatedParentElement.id, true);

                // update synonym list of parent element in the saved query context
                updateSynonyms(synonymType, duplicatedParentElement, [...duplicatedParentElementSynonymList, newSynonym], duplicatedSavedQuery);
            });
        }
    };

    const addSynonymByInputElement = async (newSynonymText: string, inputRef: HTMLInputElement): Promise<void> => {
        await addSynonym(newSynonymText);
        
        // empty add new synonym input
        inputRef.value = "";
    };

    const deleteSynonym = async (synonymToDelete: TSynonymDTO): Promise<void> => {
        // safety-checks
        if (!savedQuery) return;

        const foundParentElement: TActionObjectDTO | TEnvironmentVariableDTO | TSpecificationUnitDTO | undefined =
            getSynonymParentElementInSavedQuery(synonymType, parentElementId, savedQuery);
        // safety-checks
        if (!foundParentElement) return;

        const newSynonyms: TSynonymDTO[] = [];
        if (savedQuery.isUserAdded && savedQuery.isOwnedByCurrentUser) {
            const isSuccess = await SynonymControllerSingleton.deleteAsync(synonymToDelete.id, true);
            // update synonym list of parent element in the saved query context
            if (isSuccess) {
                // filter synonym list parent props by removing the synonym
                for (const synonym of synonyms) {
                    if (synonym.id !== synonymToDelete.id) {
                        newSynonyms.push(synonym);
                    }
                }

                updateSynonyms(synonymType, foundParentElement, [...newSynonyms]);
            }
        } else {
            duplicateSavedQuery(caseState.selectedOriginalTechnologyId, caseState.selectedCaseId, async (duplicatedSavedQuery: TSavedQueryTempDTO): Promise<void> => {
                // safety-checks
                if (!duplicatedSavedQuery) return;

                const duplicatedParentElement: TActionObjectDTO | TEnvironmentVariableDTO | TSpecificationUnitDTO | undefined =
                    findDuplicatedParentElementIdInSavedQuery(synonymType, foundParentElement, duplicatedSavedQuery);
                // safety-checks
                if (!duplicatedParentElement) return;

                const duplicatedParentElementSynonymList: TSynonymDTO[] | undefined =
                    getParentElementSynonymList(synonymType, duplicatedParentElement);
                // safety-checks
                if (!duplicatedParentElementSynonymList) return;

                const newSynonymToDelete: TSynonymDTO | undefined = findDuplicatedSynonymInSynonymList(synonymToDelete.text, duplicatedParentElementSynonymList);
                // safety-checks
                if (!newSynonymToDelete) return;

                const isSuccess = await SynonymControllerSingleton.deleteAsync(newSynonymToDelete.id, true);
                // update synonym list of parent element in the saved query context
                if (isSuccess) {
                    for (const synonym of duplicatedParentElementSynonymList) {
                        if (synonym.id !== newSynonymToDelete.id) {
                            newSynonyms.push(synonym);
                        }
                    }

                    updateSynonyms(synonymType, duplicatedParentElement, [...newSynonyms], duplicatedSavedQuery);
                }
            });
        }
    };

    const handleIsSynonymRequired = async (isSynonymRequired: boolean, synonymUpdated: TSynonymDTO): Promise<void> => {
        // safety-checks
        if (!savedQuery) return;

        const foundParentElement: TActionObjectDTO | TEnvironmentVariableDTO | TSpecificationUnitDTO | undefined =
            getSynonymParentElementInSavedQuery(synonymType, parentElementId, savedQuery);
        // safety-checks
        if (!foundParentElement) return;

        if (savedQuery.isUserAdded && savedQuery.isOwnedByCurrentUser) {
            synonymUpdated.required = isSynonymRequired;
            const isSuccess = await SynonymControllerSingleton.updateAsync(synonymUpdated, true);
            // update synonym list of parent element in the saved query context
            if (isSuccess) {
                for (const synonym of synonyms) {
                    if (synonym.id === synonymUpdated.id) {
                        synonym.required = isSynonymRequired;
                    }
                }

                updateSynonyms(synonymType, foundParentElement, [...synonyms]);
            }
        } else {
            duplicateSavedQuery(caseState.selectedOriginalTechnologyId, caseState.selectedCaseId, async (duplicatedSavedQuery: TSavedQueryTempDTO): Promise<void> => {
                // safety-checks
                if (!duplicatedSavedQuery) return;

                const duplicatedParentElement: TActionObjectDTO | TEnvironmentVariableDTO | TSpecificationUnitDTO | undefined =
                    findDuplicatedParentElementIdInSavedQuery(synonymType, foundParentElement, duplicatedSavedQuery);
                // safety-checks
                if (!duplicatedParentElement) return;

                const duplicatedParentElementSynonymList: TSynonymDTO[] | undefined =
                    getParentElementSynonymList(synonymType, duplicatedParentElement);
                // safety-checks
                if (!duplicatedParentElementSynonymList) return;

                const synonymToUpdate: TSynonymDTO | undefined = findDuplicatedSynonymInSynonymList(synonymUpdated.text, duplicatedParentElementSynonymList);
                // safety-checks
                if (!synonymToUpdate) return;
                synonymToUpdate.required = isSynonymRequired;
                const isSuccess = await SynonymControllerSingleton.updateAsync(synonymToUpdate, true);

                // update synonym list of parent element in the saved query context
                if (isSuccess) {
                    for (const synonym of duplicatedParentElementSynonymList) {
                        if (synonym.id === synonymToUpdate.id) {
                            synonym.required = isSynonymRequired;
                        }
                    }

                    updateSynonyms(synonymType, duplicatedParentElement, [...duplicatedParentElementSynonymList], duplicatedSavedQuery);
                }
            });
        }
    };

    const editSynonym = async (id: number, newValue: string, oldValue: string): Promise<void> => {
        // safety-checks
        if (!savedQuery) return;

        const foundParentElement: TActionObjectDTO | TEnvironmentVariableDTO | TSpecificationUnitDTO | undefined =
            getSynonymParentElementInSavedQuery(synonymType, parentElementId, savedQuery);
        // safety-checks
        if (!foundParentElement) return;

        if (savedQuery.isUserAdded && savedQuery.isOwnedByCurrentUser) {
            const foundSynonym: TSynonymDTO | undefined = getSynonymByIdInList(id, synonyms);
            // safety-checks
            if (!foundSynonym) return;

            foundSynonym.text = newValue;
            const isSuccess = await SynonymControllerSingleton.updateAsync(foundSynonym, true);
            // update synonym list of parent element in the saved query context
            if (isSuccess) {
                for (const synonym of synonyms) {
                    if (synonym.id === id) {
                        synonym.text = newValue;
                    }
                }

                updateSynonyms(synonymType, foundParentElement, [...synonyms]);
            }
        } else {
            duplicateSavedQuery(caseState.selectedOriginalTechnologyId, caseState.selectedCaseId, async (duplicatedSavedQuery: TSavedQueryTempDTO): Promise<void> => {
                // safety-checks
                if (!duplicatedSavedQuery) return;

                const duplicatedParentElement: TActionObjectDTO | TEnvironmentVariableDTO | TSpecificationUnitDTO | undefined =
                    findDuplicatedParentElementIdInSavedQuery(synonymType, foundParentElement, duplicatedSavedQuery);
                // safety-checks
                if (!duplicatedParentElement) return;

                const duplicatedParentElementSynonymList: TSynonymDTO[] | undefined =
                    getParentElementSynonymList(synonymType, duplicatedParentElement);
                // safety-checks
                if (!duplicatedParentElementSynonymList) return;

                const synonymToUpdate: TSynonymDTO | undefined = findDuplicatedSynonymInSynonymList(oldValue, duplicatedParentElementSynonymList);
                // safety-checks
                if (!synonymToUpdate) return;
                synonymToUpdate.text = newValue;
                const isSuccess = await SynonymControllerSingleton.updateAsync(synonymToUpdate, true);

                // update synonym list of parent element in the saved query context
                if (isSuccess) {
                    for (const synonym of duplicatedParentElementSynonymList) {
                        if (synonym.id === synonymToUpdate.id) {
                            synonym.text = newValue;
                        }
                    }

                    updateSynonyms(synonymType, duplicatedParentElement, [...duplicatedParentElementSynonymList], duplicatedSavedQuery);
                }
            });
        }
    };

    const showGeneratedSynonymsPopup = (event: React.FocusEvent): void => {
        event.stopPropagation();
        event.preventDefault();
        calculateDropdownListPosition(event.currentTarget);
    };

    const getDropdownListPosition = (): CSSProperties => {
        const dropdownListPositionCss: CSSProperties = {};
        dropdownListPositionCss.left = `${dropdownListPosition.x + 28}px`;
        dropdownListPositionCss.top = `${dropdownListPosition.y - 4}px`;
        dropdownListPositionCss.padding = "0.5rem";
        return dropdownListPositionCss;
    };

    return (
        <div className={styles.findestSynonymsContainer}>
            <FindestTextBox isBorderless={true} placeholder={"Synonym <Enter>"} onEnter={addSynonymByInputElement} />

            { isGenerationPopupOpen ?
                <GeneratedSynonymsPopup parentElementId={parentElementId.toString()} parentElementText={parentSynonymText} 
                    synonymType={synonymType} addSynonym={addSynonym} changePopupOpen={setIsGenerationPopupOpen} 
                    isHistoricalEnabled={false} extraStyle={getDropdownListPosition()} />
                : null
            } 
            { isPartOfTermGroup && mainTermGroupValue ? 
                <Synonym synonym={{ id: -2, required: true, synonymType: SynonymTypeEnum.Phrase, text: mainTermGroupValue }} />
                : null 
            }
            {synonyms.map((synonym: TSynonymDTO) => {
                return (
                    <Synonym key={synonym.id} synonym={synonym} handleIsSynonymRequiredOnChange={handleIsSynonymRequired} 
                        handleDeleteSynonym={deleteSynonym} handleEditSynonym={editSynonym} 
                        handleOnFocusSynonym={showGeneratedSynonymsPopup} setSynonymPopupText={setParentSynonymText} />
                );
            })}

            {FeatureToggleConstants.SynonymGroups && !isPartOfTermGroup ?
                <CreateSynonymGroup synonymType={synonymType} parentElementId={parentElementId} />
                : null}
        </div>
    );
};